import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { ShieldCheck, LockKey, ArrowLeft, Fingerprint, FilePdf } from "@phosphor-icons/react";

const CARD_LAYOUT = [
  { section: "Role Definition", rows: [
    { key: "who_i_am", label: "Who I Am" },
    { key: "role_position_title", label: "Role, Position, Title" },
    { key: "behavior", label: "Behavior" },
    { key: "mode", label: "Mode" },
    { key: "style", label: "Style" },
  ]},
  { section: "AI Logistics", rows: [
    { key: "identity_of_ai", label: "Identity of AI" },
    { key: "ai_engine", label: "AI Engine" },
    { key: "api_key_enabled", label: "API Key Enabled" },
  ]},
  { section: "Continuity Protocol", rows: [{ key: "continuity_protocol", label: "" }] },
  { section: "Company", rows: [
    { key: "company", label: "Company" },
    { key: "founder", label: "Founder" },
  ]},
  { section: "Team Members", rows: [{ key: "team_members", label: "" }] },
  { section: "Additional Information", rows: [{ key: "additional_information", label: "" }] },
];

export default function CardView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);

  useEffect(() => {
    api
      .get(`/cards/${id}`)
      .then((r) => setCard(r.data))
      .catch((err) => {
        toast.error(apiError(err));
        navigate("/dashboard");
      });
  }, [id]);

  // Copy protection: block copy, cut, contextmenu
  const block = useCallback((e) => {
    e.preventDefault();
    toast.error("Copy protection active — this handoff is locked");
  }, []);

  useEffect(() => {
    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    document.addEventListener("contextmenu", block);
    return () => {
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("contextmenu", block);
    };
  }, [block]);

  if (!card) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <span className="font-mono-p text-sm text-zinc-600 animate-pulse">DECRYPTING...</span>
      </div>
    );
  }

  const c = card.content;

  const downloadPdf = () => {
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 48;
      const labelW = 150;
      const valX = margin + labelW + 12;
      const valW = pageW - valX - margin;
      let y = margin;
      const ensure = (h) => { if (y + h > pageH - margin) { doc.addPage(); y = margin; } };

      doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(20);
      doc.text("AI iD Card", margin, y); y += 22;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(120);
      doc.text(`${card.prid}   ·   v${card.version}`, margin, y); y += 20;

      const heading = c.identity_of_ai || c.who_i_am || "AI iD Card";
      doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(20);
      doc.text(doc.splitTextToSize(heading, pageW - margin * 2), margin, y); y += 18;
      const sub = [c.role_position_title, c.ai_engine].filter(Boolean).join("   ·   ");
      if (sub) {
        doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(120);
        doc.text(sub, margin, y); y += 16;
      }
      doc.setDrawColor(210); doc.line(margin, y, pageW - margin, y); y += 22;

      CARD_LAYOUT.forEach((sec) => {
        ensure(34);
        doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(0, 150, 170);
        doc.text(sec.section.toUpperCase(), margin, y); y += 16;
        doc.setTextColor(20);
        sec.rows.forEach((r) => {
          const val = (c[r.key] || "—").toString();
          const wrapW = r.label ? valW : pageW - margin * 2;
          const lines = doc.splitTextToSize(val, wrapW);
          const rowH = Math.max(16, lines.length * 13) + 8;
          ensure(rowH);
          if (r.label) {
            doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(120);
            doc.text(r.label, margin, y);
            doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(30);
            doc.text(lines, valX, y);
          } else {
            doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(30);
            doc.text(lines, margin, y);
          }
          y += rowH;
        });
        y += 8;
      });

      ensure(28);
      doc.setDrawColor(210); doc.line(margin, y, pageW - margin, y); y += 16;
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(140);
      doc.text("Encrypted  ·  Copy-protected  ·  Single-use PRID", margin, y);

      doc.save(`AI-iD-Card-${card.prid || "card"}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not generate PDF");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 py-10 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            data-testid="view-back-btn"
            onClick={() => navigate(`/card/${id}`)}
            className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-zinc-500 hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft size={14} /> Back to editor
          </button>
          <button
            data-testid="download-pdf-btn"
            onClick={downloadPdf}
            className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest border border-cyan-400/40 text-cyan-400 px-4 py-2.5 hover:bg-cyan-400 hover:text-black transition-colors"
          >
            <FilePdf size={14} weight="bold" /> Download PDF
          </button>
        </div>

        <div
          data-testid="readonly-card"
          className="copy-protected scanlines relative border border-cyan-400/20 bg-[#090A0C] p-10 overflow-hidden"
        >
          {/* watermark */}
          <div className="pointer-events-none select-none absolute inset-0 flex items-center justify-center opacity-[0.04] z-0">
            <span className="font-mono-p font-bold text-6xl rotate-[-20deg] whitespace-nowrap tracking-tighter">
              {card.prid} · {card.prid} · {card.prid}
            </span>
          </div>

          <div className="relative z-10">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-6">
              <div className="flex items-center gap-2">
                <ShieldCheck size={22} weight="duotone" className="text-cyan-400" />
                <span className="font-mono-p font-bold tracking-tight text-sm">
                  AI iD Card
                </span>
              </div>
              <span className="font-mono-p text-[11px] tracking-wider text-cyan-400 border border-cyan-400/30 px-2 py-1 flex items-center gap-1.5 drop-shadow-[0_0_8px_rgba(0,240,255,0.3)]">
                <Fingerprint size={12} /> {card.prid}
              </span>
            </div>

            <h1 className="mt-8 font-mono-p font-bold text-3xl tracking-tight">
              {c.identity_of_ai || c.who_i_am || "AI iD Card"}
            </h1>
            <p className="mt-2 font-mono-p text-sm text-zinc-500">
              {c.role_position_title || "No role"} {c.ai_engine ? `· ${c.ai_engine}` : ""} · v{card.version}
            </p>

            <div className="mt-8 border border-zinc-800 rounded-lg overflow-hidden" data-testid="ai-id-card-doc">
              <div className="grid grid-cols-[minmax(150px,0.9fr)_2fr] bg-zinc-800/60 font-mono-p text-[11px] tracking-widest text-cyan-400">
                <div className="px-4 py-2.5 border-r border-zinc-800">AI iD Card</div>
                <div className="px-4 py-2.5">Details</div>
              </div>
              {CARD_LAYOUT.map((sec) => (
                <div key={sec.section} data-testid={`view-section-${sec.section.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="grid grid-cols-[minmax(150px,0.9fr)_2fr] border-t border-zinc-800 bg-[#0c0e12]">
                    <div className="px-4 py-2 font-mono-p text-xs uppercase tracking-wider text-zinc-300 border-r border-zinc-800">{sec.section}</div>
                    <div />
                  </div>
                  {sec.rows.map((r) => (
                    <div key={r.key} className="grid grid-cols-[minmax(150px,0.9fr)_2fr] border-t border-zinc-800/60">
                      <div className="px-4 py-2.5 text-xs text-zinc-500 border-r border-zinc-800/60">{r.label}</div>
                      <div className="px-4 py-2.5 text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed" data-testid={`view-${r.key}`}>
                        {c[r.key] || <span className="text-zinc-700">—</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center gap-2 border-t border-zinc-800 pt-6 font-mono-p text-[11px] uppercase tracking-widest text-zinc-600">
              <LockKey size={13} className="text-cyan-400" />
              Encrypted · Copy-protected · Single-use PRID
            </div>
          </div>
        </div>

        <p className="text-center mt-6 font-mono-p text-xs text-zinc-600">
          Upload / show this card to your next AI to resume continuity.
        </p>
      </div>
    </div>
  );
}
